import GoogletagFacade from '../googletagFacade/GoogletagFacade'
import { queryParamHas } from '../helpers/getQueryParam'
import { USP } from './USP'
import WebModel from '../models/WebModel'
import { writeCss } from '../helpers/DOM'

const isCcpaTest = queryParamHas('test', 'ccpa')
const OPT_OUT = '1YYY'

export const dataNameCCPA: IDataNameCCPA = {
  button: 'mediavine-ccpa-button',
  checkbox: 'mediavine-ccpa-modal-checkbox',
  close: 'mediavine-ccpa-modal-close',
  link: 'mediavine-ccpa-modal-link'
}

export default class CCPA {
  private _window: Window = window
  private ccpaColor: string
  private ccpaPosition: string
  private ccpaModalEnabled: boolean = true
  private ccpaText: string
  private checkbox: HTMLInputElement
  private enableCcpa: boolean = false
  private footerSelector: string | undefined
  private modal: HTMLElement
  private privacyPolicyLink: string
  private uspOptOutListeners: Array<Function> = []
  private uspOptOutStatus: boolean = false

  constructor(model: WebModel) {
    const {
      ccpa,
      ccpa_link_loc,
      ccpa_modal_enabled,
      custom_ccpa_text,
      custom_cmp_color,
      footer_selector,
      privacy_policy_link
    } = model

    if (ccpa === '1' || isCcpaTest) {
      this.ccpaColor = custom_cmp_color
      this.ccpaModalEnabled = ccpa_modal_enabled
      this.ccpaPosition = ccpa_link_loc
      this.ccpaText = custom_ccpa_text
      this.footerSelector = footer_selector
      this.enableCcpa = true
      this.privacyPolicyLink = privacy_policy_link
      if (USP.uspString === OPT_OUT) {
        this.uspOptOutStatus = true
      } else {
        USP.userHasReceivedExplictNotice()
      }
      this.handleUserStatus()
    }

    // Expose public API to toggle uspOptOutStatus
    this._window.$mediavine.web.uspOptOut = this.uspOptOut.bind(this)
    // Expose public API to listen to uspOptOutStatus changes
    this._window.$mediavine.web.uspOptOutEvent = this.uspOptOutEvent.bind(this)
    // Dispatch an event to fire any publisher listener functions for these APIs
    this._window.dispatchEvent(new CustomEvent('mediavineUspReady'))
  }

  public async initModal(): Promise<any> {
    if (this.ccpaModalEnabled && this.enableCcpa) {
      try {
        const {
          buttonHTML,
          footerHTML,
          modalHTML,
          styles
        } = await this.loadAssets()

        const color = this.ccpaColor
        const position = this.ccpaPosition

        switch (position) {
          case 'none':
            // noop
            break
          case 'footer':
            this.insertFooter(footerHTML)
            break
          case 'left':
          case 'right':
          default:
            this.insertButton(buttonHTML)
            break
        }

        this.insertModal(modalHTML)
        writeCss(styles({ color, position }))

        // listen for all CCPA related clicks
        this._window.document.addEventListener(
          'click',
          this.handleCCPAClicks.bind(this)
        )
      } catch (error) {
        /* noop */
      }
    }
  }

  /** Delegates all CCPA related click events */
  private handleCCPAClicks(event: MouseEvent): void {
    const target = event.target as HTMLElement
    const { name } = target.dataset

    switch (name) {
      case dataNameCCPA.button:
        this.modal.style.display = 'block'
        break
      case dataNameCCPA.checkbox:
        this.uspOptOutStatus = this.checkbox.checked
        break
      case dataNameCCPA.close:
        this.modal.style.display = 'none'
        break
      case dataNameCCPA.link:
        if (this.checkbox.type === 'hidden') {
          this.checkbox.type = 'checkbox'
        }
        if (this.checkbox.type === 'checkbox') {
          this.checkbox.checked = !this.checkbox.checked
          this.uspOptOutStatus = !this.uspOptOutStatus
        }
        this.handleUserStatus()
        break
    }
  }

  /** Toggles USP methods and Google Tag privacy settings, fires any publisher listeners */
  private handleUserStatus(): void {
    if (this.uspOptOutStatus) {
      USP.userHasOptedOut()
    } else {
      USP.userHasReceivedExplictNotice()
    }

    this.uspOptOutListeners.forEach((listener) => {
      // ensure publisher provided listener is a function
      if (listener.apply && listener.call) {
        try {
          const status: IUspOptOutEvent = {
            uspOptOutStatus: this.uspOptOutStatus
          }
          listener(status)
        } catch (e) {
          /* noop */
        }
      }
    })

    GoogletagFacade.onReady(() => {
      this._window.googletag.pubads().setPrivacySettings({
        restrictDataProcessing: this.uspOptOutStatus
      })
    }).catch(() => {
      /* noop */
    })
  }

  /** Creates and inserts a CCPA link as a button in left (default) or right positions */
  private insertButton(buttonHTML: Function): void {
    const link = this._window.document.createElement('div')
    link.innerHTML = buttonHTML(dataNameCCPA)
    this._window.document.body.insertAdjacentElement('afterbegin', link)
  }

  /** Creates and inserts a CCPA link in the "Comscore footer" */
  private insertFooter(footerHTML: Function): void {
    const link = this._window.document.createElement('div')
    link.innerHTML = footerHTML(dataNameCCPA)
    const footer =
      this.footerSelector &&
      this._window.document.querySelector(this.footerSelector)
    const position = footer ? 'afterend' : 'beforeend'
    const element = footer || this._window.document.body
    element.insertAdjacentElement(position, link)
  }

  /** Creates and inserts the CCPA modal */
  private insertModal(modalHTML: Function): void {
    this.modal = this._window.document.createElement('div')
    this.modal.innerHTML = modalHTML(
      dataNameCCPA,
      this.privacyPolicyLink,
      this.ccpaText
    )
    this.modal.style.display = 'none'
    this.checkbox = this.modal.querySelector(
      `*[data-name='${dataNameCCPA.checkbox}']`
    ) as HTMLInputElement
    this._window.document.body.insertAdjacentElement('afterbegin', this.modal)
    // pre populate the checkbox if a user had already opted out
    if (this.uspOptOutStatus) {
      this.checkbox.type = 'checkbox'
      this.checkbox.checked = true
    }
  }

  /** Dynamically imports CCPA HTML and CSS assets */
  private loadAssets(): Promise<any> {
    return import(/* webpackChunkName: "ccpaAssets" */ './assets')
  }

  /** Method exposed via window.$mediavine.web.uspOptOut  */
  private uspOptOut(bool: boolean): void {
    this.uspOptOutStatus = bool
    if (this.checkbox) {
      this.checkbox.checked = this.uspOptOutStatus
      this.checkbox.type = this.uspOptOutStatus ? 'checkbox' : 'hidden'
    }
    this.handleUserStatus()
  }

  /** Method exposed via window.$mediavine.web.uspOptOutEvent  */
  private uspOptOutEvent(callback: Function): void {
    this.uspOptOutListeners.push(callback)
  }
}

interface IUspOptOutEvent {
  uspOptOutStatus: boolean
}

export interface IDataNameCCPA {
  button: string
  checkbox: string
  close: string
  link: string
}
